/**
 * 切片配置输入组件
 *
 * 用于上传文件时配置切片参数（智能客服 FAQ 场景优化）
 */

import React, { useState, useEffect } from 'react';
import { Button, InputNumber, Slider, Space, Tag, Typography, Alert, Row, Col, Card } from 'antd';
import {
    InfoCircleOutlined,
    SettingOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

export interface ChunkConfig {
    /** 切片大小（字符数） */
    chunk_size: number;
    /** 切片重叠（字符数） */
    chunk_overlap: number;
    /** 是否自动计算 */
    auto_calculated?: boolean;
}

export interface ChunkConfigInputProps {
    /** 文件大小（字节） */
    fileSize?: number;
    /** 文件名 */
    fileName?: string;
    /** 当前配置 */
    value?: ChunkConfig;
    /** 配置变更回调 */
    onChange?: (config: ChunkConfig) => void;
    /** 是否禁用 */
    disabled?: boolean;
}

/** FAQ 场景默认推荐配置 */
const FAQ_DEFAULTS = {
    chunk_size: 256,
    chunk_overlap: 50,
    reason: 'FAQ/客服话术场景：极细颗粒度，精准命中问答对',
    range: [100, 500] as [number, number],
};

/**
 * 格式化文件大小
 */
function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * 切片配置输入组件
 */
export const ChunkConfigInput: React.FC<ChunkConfigInputProps> = ({
    fileSize,
    fileName,
    value,
    onChange,
    disabled = false,
}) => {
    const [chunkSize, setChunkSize] = useState<number>(value?.chunk_size || FAQ_DEFAULTS.chunk_size);
    const [chunkOverlap, setChunkOverlap] = useState<number>(value?.chunk_overlap || FAQ_DEFAULTS.chunk_overlap);
    const [autoCalculated, setAutoCalculated] = useState<boolean>(value?.auto_calculated ?? true);

    // 验证配置
    const isValid = chunkSize >= 100 && chunkSize <= 2000 && chunkOverlap >= 0 && chunkOverlap < chunkSize;

    // 触发变更
    useEffect(() => {
        if (onChange && isValid) {
            onChange({
                chunk_size: chunkSize,
                chunk_overlap: chunkOverlap,
                auto_calculated: autoCalculated,
            });
        }
    }, [chunkSize, chunkOverlap, autoCalculated, onChange, isValid]);

    // 使用推荐值
    const handleUseRecommended = () => {
        setChunkSize(FAQ_DEFAULTS.chunk_size);
        setChunkOverlap(FAQ_DEFAULTS.chunk_overlap);
        setAutoCalculated(true);
    };

    // 重置为默认值
    const handleReset = () => {
        setChunkSize(FAQ_DEFAULTS.chunk_size);
        setChunkOverlap(FAQ_DEFAULTS.chunk_overlap);
        setAutoCalculated(true);
    };

    return (
        <Card
            size="small"
            title={
                <Space>
                    <SettingOutlined />
                    <Text strong>高级选项 - 切片配置</Text>
                </Space>
            }
            type="inner"
            bordered
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* 文件信息 */}
                {fileName && fileSize && (
                    <Alert
                        message={
                            <Space>
                                <Text>文件：{fileName}</Text>
                                <Tag color="blue">{formatFileSize(fileSize)}</Tag>
                            </Space>
                        }
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                    />
                )}

                {/* 推荐配置 */}
                {autoCalculated && (
                    <Alert
                        message={
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Space>
                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                    <Text>推荐切片大小：{FAQ_DEFAULTS.chunk_size} 字符</Text>
                                </Space>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {FAQ_DEFAULTS.reason}（范围：{FAQ_DEFAULTS.range[0]} - {FAQ_DEFAULTS.range[1]} 字符）
                                </Text>
                            </Space>
                        }
                        type="success"
                        showIcon
                        action={
                            <Button size="small" type="link" onClick={() => setAutoCalculated(false)}>
                                手动调整
                            </Button>
                        }
                    />
                )}

                {/* 切片大小 */}
                <div>
                    <Space style={{ marginBottom: 8 }}>
                        <Text strong>切片大小</Text>
                        {!autoCalculated && (
                            <Button size="small" type="link" onClick={handleUseRecommended}>
                                使用推荐值
                            </Button>
                        )}
                    </Space>
                    <Row align="middle" gutter={16}>
                        <Col flex="auto">
                            <Slider
                                min={100}
                                max={2000}
                                step={50}
                                value={chunkSize}
                                onChange={(val) => {
                                    setChunkSize(val);
                                    setAutoCalculated(false);
                                }}
                                disabled={disabled || autoCalculated}
                                marks={{
                                    100: '100',
                                    256: '256',
                                    500: '500',
                                    1000: '1000',
                                    2000: '2000',
                                }}
                            />
                        </Col>
                        <Col>
                            <InputNumber
                                min={100}
                                max={2000}
                                step={50}
                                value={chunkSize}
                                onChange={(val) => {
                                    if (val !== null) {
                                        setChunkSize(val);
                                        setAutoCalculated(false);
                                    }
                                }}
                                disabled={disabled || autoCalculated}
                                addonAfter="字符"
                                style={{ width: 140 }}
                            />
                        </Col>
                    </Row>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        每个切片的最大字符数。较小的值适合 FAQ 问答检索，较大的值适合长文档。
                    </Text>
                </div>

                {/* 切片重叠 */}
                <div>
                    <Space style={{ marginBottom: 8 }}>
                        <Text strong>切片重叠</Text>
                    </Space>
                    <Row align="middle" gutter={16}>
                        <Col flex="auto">
                            <Slider
                                min={0}
                                max={200}
                                step={10}
                                value={chunkOverlap}
                                onChange={(val) => setChunkOverlap(val)}
                                disabled={disabled}
                                marks={{
                                    0: '0',
                                    50: '50',
                                    100: '100',
                                    150: '150',
                                    200: '200',
                                }}
                            />
                        </Col>
                        <Col>
                            <InputNumber
                                min={0}
                                max={200}
                                step={10}
                                value={chunkOverlap}
                                onChange={(val) => {
                                    if (val !== null) setChunkOverlap(val);
                                }}
                                disabled={disabled}
                                addonAfter="字符"
                                style={{ width: 140 }}
                            />
                        </Col>
                    </Row>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        相邻切片之间的重叠字符数，避免上下文被切断。建议为切片大小的 15% 左右。
                    </Text>
                </div>

                {/* 操作按钮 */}
                <Space>
                    {!autoCalculated && (
                        <Button size="small" onClick={handleReset}>
                            重置为默认
                        </Button>
                    )}
                    {!isValid && (
                        <Text type="danger" style={{ fontSize: 12 }}>
                            <InfoCircleOutlined /> 配置无效：切片重叠必须小于切片大小
                        </Text>
                    )}
                </Space>
            </Space>
        </Card>
    );
};
